/**
 * The two rules a Relationship Source adds on top of "follow one relationship from $context", kept in
 * one place because two resolvers read the same stored Source: the app's (resolveCollection.js, over
 * graph nodes and edges) and the public site's (siteModel.resolveSiteCollection, over item rows, with
 * no links table). Each passes its own way of reading a value or taking a step, and this decides what
 * the Source means.
 *
 * A STEP AFTER A STEP. A source may carry `then`: another step, taken from every item the first one
 * reached — and `then` may carry its own. "Other programs from this program's host" is
 *   { via: 'made_by', then: { via: 'made_by', direction: 'incoming' } }
 * — out to the host, then back in to everything the host offers. The item the walk started from is
 * the resolver's to drop, as it already drops it from a one-step walk.
 *
 * SHARES VALUES WITH IT. A source may instead carry `similar: { fields: [...] }`: every item that shares
 * at least one value with the context item on EACH listed field, ranked by how many values it shares in
 * all. "Same level, and at least one category in common" — the old systemschange.directory's Related
 * Programs — is `{ similar: { fields: ['level', 'categories'] } }`. A field the context item leaves
 * empty matches nothing, so an item with no category has no similar items rather than every item.
 *
 * Pure — no React, no data service — so `node --test` covers it.
 */

/** A field's value as comparable words: select options, linked ids, a string, a number. */
export function matchValues(value) {
  const list = Array.isArray(value) ? value : value == null || value === '' ? [] : [value];
  const out = [];
  for (const x of list) {
    const v = x && typeof x === 'object' ? (x.id ?? x.name ?? x.value ?? x.label) : x;
    if (typeof v === 'number' && Number.isFinite(v)) out.push(String(v));
    else if (typeof v === 'string' && v.trim()) out.push(v.trim().toLowerCase());
  }
  return [...new Set(out)];
}

/**
 * Every item one source reaches from `fromId`, taking its `then` steps in turn.
 *
 * @param {string} fromId
 * @param {object} source   a stored source: `{ via, viaKind?, linkTypeId?, direction?, then? }`
 * @param {(id: string, step: object) => Iterable<string>} stepIds  the resolver's one step
 * @returns {Set<string>}
 */
export function walkSource(fromId, source, stepIds) {
  let frontier = new Set([fromId]);
  const seen = new Set();
  for (let step = source; step && typeof step === 'object'; step = step.then) {
    if (seen.has(step)) break;   // a hand-written cycle in `then` must not spin
    seen.add(step);
    const next = new Set();
    for (const id of frontier) for (const x of stepIds(id, step) || []) if (x) next.add(x);
    frontier = next;
    if (!frontier.size) break;
  }
  return frontier;
}

/**
 * SIMILAR IN MEANING. `{ similar: { by: 'meaning' } }` reads the context item's `similar_items`: its
 * nearest items of the same type by embedding, nearest first, which the embedding job writes
 * (migration 20261118153907). They outrank any `fields` match, nearest first, so a collection can
 * list meaning first and fall back on shared values for an item not yet embedded.
 */
export const SIMILAR_ITEMS_KEY = 'similar_items';
const MEANING_BASE = 1000;

/** Is this a `similar: { by: 'meaning' }` source? */
export const isMeaningSource = (source) => source?.similar?.by === 'meaning';

/** The meaning source's scores: the context item's stored neighbours, the nearest scoring highest. */
export function meaningScores(context, read) {
  const scores = new Map();
  const raw = context ? read(context, SIMILAR_ITEMS_KEY) : null;
  const ids = (Array.isArray(raw) ? raw : []).map((x) => (x && typeof x === 'object' ? x.id : x)).filter((x) => typeof x === 'string' && x);
  ids.forEach((id, i) => { if (id !== context.id && !scores.has(id)) scores.set(id, MEANING_BASE + ids.length - i); });
  return scores;
}

/** The fields a `similar` source compares, or null when it is not one. */
export function similarFields(source) {
  const fields = source?.similar?.fields;
  return Array.isArray(fields) ? fields.filter((f) => typeof f === 'string' && f) : null;
}

/**
 * The items that share at least one value with `context` on every field, each with how many values it
 * shares across them all. The context item itself is never one.
 *
 * @param {object} context
 * @param {object[]} candidates
 * @param {string[]} fields
 * @param {(item: object, field: string) => unknown} read  the resolver's way of reading a field
 * @returns {Map<string, number>}  item id → shared values
 */
export function similarScores(context, candidates, fields, read) {
  const scores = new Map();
  if (!context || !fields.length) return scores;
  const wanted = fields.map((f) => new Set(matchValues(read(context, f))));
  if (wanted.some((s) => !s.size)) return scores;
  for (const c of candidates) {
    if (!c || c.id === context.id) continue;
    let total = 0;
    let every = true;
    for (let i = 0; i < fields.length; i++) {
      const shared = matchValues(read(c, fields[i])).filter((v) => wanted[i].has(v)).length;
      if (!shared) { every = false; break; }
      total += shared;
    }
    if (every) scores.set(c.id, total);
  }
  return scores;
}

/**
 * The order a relationship's results read in: items a walked source reached first, as the resolver
 * found them, then items only a `similar` source found, most shared values first. Stable otherwise.
 */
export function rankBySimilarity(items, walked, scores) {
  const tier = (it) => (walked.has(it.id) ? 0 : 1);
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => tier(a.it) - tier(b.it)
      || (tier(a.it) ? (scores.get(b.it.id) || 0) - (scores.get(a.it.id) || 0) : 0)
      || a.i - b.i)
    .map((x) => x.it);
}
